import * as trace from 'owa-trace';
import { checkForWhiteListedDiff, WhiteListedDiff } from './utils/checkForWhiteListedDiff';
import { getUserTimeZone } from 'owa-session-store';
import { OwaWorkload } from 'owa-workloads';
import { RefreshStrategy } from 'mini-settings/lib/schema/RefreshStrategy';
import { SessionData } from 'owa-service/lib/types/SessionData';
import { SettingsMetaData } from 'mini-settings/lib/schema/SettingsMetaData';
import { storeSessionDetailsInSessionStorage } from './storeSessionDetailsInSessionStorage';
import { USE_STARTUP_DATA_FROM_EXTENSION } from 'extension-communication/lib/constants/useDataFromExtension';
import {
    getItemFromSessionStorage,
    setItemInSessionStorage,
    clearSessionStorage,
} from 'mini-session-storage';

let cachedSessionDataFromNetwork: SessionData = null;

/**
 * Create deep copy of the sessionData object returned from network call.
 * Deep copy is required here, as the object is mutated by various functions from shared start
 * and we need the complete network response as it is.
 */
export function saveSessionDataFromNetwork(sessionData: SessionData) {
    try {
        cachedSessionDataFromNetwork = JSON.parse(JSON.stringify(sessionData));
    } catch (e) {
        trace.errorThatWillCauseAlert('SessionStorage: Unable to parse SessionData' + e);
    }
}

/**
 * Rehydrates sessionData with required properties that are pruned in setUserConfiguration
 * in sharedStart.
 * @param sessionData
 */
export function rehydrateSessionDataFromSessionStorage(
    sessionData: SessionData,
    hasDataInSessionStorage: boolean
) {
    const sessionDataToRead = hasDataInSessionStorage
        ? getItemFromSessionStorage('session')
        : cachedSessionDataFromNetwork;

    sessionData.owaUserConfig.Favorites = sessionDataToRead?.owaUserConfig.Favorites;
    sessionData.owaUserConfig.SessionSettings.DefaultFolderIds =
        sessionDataToRead?.owaUserConfig.SessionSettings.DefaultFolderIds;
    sessionData.owaUserConfig.SessionSettings.DefaultFolderNames =
        sessionDataToRead?.owaUserConfig.SessionSettings.DefaultFolderNames;
}

/**
 * Sets the session data in session storage
 * @param sessionData sessionData to be set
 * @param workload differentiates which app is using this
 */ export function setSessionData(
    sessionData: SessionData,
    workload: OwaWorkload,
    hasDataInSessionStorage: boolean
) {
    // Session data related
    if (workload === OwaWorkload.Calendar) {
        (sessionData as any).getCalendarFolders &&
            setItemInSessionStorage('getCalendarFolders', (sessionData as any).getCalendarFolders);
    }

    if (USE_STARTUP_DATA_FROM_EXTENSION && workload === OwaWorkload.Mail) {
        const { findFolders, findConversation, findMailFolderItem } = sessionData;
        findFolders && setItemInSessionStorage('findFolders', findFolders);
        findConversation && setItemInSessionStorage('findConversation', findConversation);
        findMailFolderItem && setItemInSessionStorage('findMailFolderItem', findMailFolderItem);
    }

    // Use the cachedSessionDataFromNetwork if the data is not yet in session storage as it is source of truth.
    // SessionData passed to this function sharedStart promise prunes few properties and we miss storing them in session storage.
    const shouldUseCachedDataForSessionStorage =
        !hasDataInSessionStorage && cachedSessionDataFromNetwork;
    const sessionDataToUse = shouldUseCachedDataForSessionStorage
        ? cachedSessionDataFromNetwork
        : sessionData;

    storeSessionDetailsInSessionStorage(sessionDataToUse);

    setItemInSessionStorage('lastSavedTime', Date.now());
}

/**
 * Common unload window actions to be done
 * @param onModuleWindowUnload Module specific unload window actions callback function
 */
function onUnloadWindowActions(onModuleWindowUnload: () => void) {
    try {
        const cachedSessionData = getItemFromSessionStorage('session');
        const userTz: string = getUserTimeZone();

        if (
            !!cachedSessionData &&
            cachedSessionData.UserOptions &&
            cachedSessionData.UserOptions.TimeZone &&
            cachedSessionData.UserOptions.TimeZone !== userTz
        ) {
            clearSessionStorage();
            return;
        }
    } catch (error) {
        // Silent fail for now
    }
    onModuleWindowUnload?.();
}

/**
 * This function does the following things:-
 * 1> Registers all the sessionStorage setters after the network sessiondata promise gets resolved.
 * 2> Checks for diff in session storage sessionData and the network sessionData.
 * 3> Also used for rebootstrapping.
 * @param initialBootstrapPromise Initial Bootstrap promise to be awaited on
 * @param bootstrapConfig Bootstrap config object used for promise await on sessionData
 * @param onUnloadWindow Callback function to be called when the window unloads
 * @param moduleRebootstrapCallback Callback function for module based bootstrapping process
 * @param workload Determines which app is using this function
 * @param hasDataInSessionStorage Boolean to determine whether data is present in session storage
 * @param onSessionData Callback function which gets called when the sessiondata promise gets returned
 * @param onSettingsChanged Callback function to determine what all things to be done if any of the settings has changed
 */
export function registerSessionStorageSetters(
    initialBootstrapPromise: Promise<any>,
    sessionPromise: Promise<SessionData>,
    onUnloadWindow: () => Promise<void>,
    moduleRebootstrapCallback: ({
        sessionData: SessionData,
        userBootSettingsData: UserBootSettings,
        bootStrings: UserBootStrings,
    }: any) => void,
    workload: OwaWorkload,
    hasDataInSessionStorage: boolean,
    whiteListedSessionDataSetting: any,
    onSessionData?: (sessionData: SessionData) => void,
    onSettingsChanged?: (
        metadata: SettingsMetaData,
        refreshStrategy: RefreshStrategy,
        sessionData: SessionData,
        onUnload?: () => Promise<void>
    ) => void
) {
    const sessionStorageUserConfig = getItemFromSessionStorage('session')?.owaUserConfig;

    // Set promise response from network request in session storage
    sessionPromise.then(sessionData => {
        onSessionData && onSessionData(sessionData);
        setSessionData(sessionData, workload, hasDataInSessionStorage);
    });

    if (hasDataInSessionStorage) {
        Promise.all([sessionPromise])
            .then(async values => {
                // tslint:disable-next-line: no-console
                console.log('AFTER NETWORK SESSION DATA', values);

                await initialBootstrapPromise; // This awaits till the initial rendering is complete with stale sessionStorage data

                // Check for diff of owaUserConfig
                const userConfigDiffs = checkForWhiteListedDiff(
                    sessionStorageUserConfig,
                    values[0].owaUserConfig,
                    whiteListedSessionDataSetting
                );

                // UserBootSettings basically will not be hydrated in the same session when the new data arrives
                // as features can run one session behind, and there is no other property here which we track.
                // isRTL is taken from owaUserConfig.SessionSettings.isUserCultureRightToLeft instead of from userbootsettings

                const allDiffs: WhiteListedDiff[] = userConfigDiffs.diffs;

                let diff: WhiteListedDiff = null;
                let refreshStrategy: RefreshStrategy = null;

                // Lets check if there are diffs which require a messaging (reload or hydrate with messaging)
                for (let currentIndex = 0; currentIndex < allDiffs.length; currentIndex++) {
                    diff = allDiffs[currentIndex];
                    if (diff?.metadata?.RefreshStrategy) {
                        refreshStrategy =
                            diff.metadata.RefreshStrategy.get(workload) ??
                            diff.metadata.RefreshStrategy.get(OwaWorkload.All);
                    } else {
                        refreshStrategy = RefreshStrategy.Reload;
                    }
                    if (refreshStrategy === RefreshStrategy.Reload) {
                        break;
                    }
                }

                // reload gets a higher priority than hydrate with messaging
                diff &&
                    onSettingsChanged &&
                    (await onSettingsChanged(
                        diff.metadata,
                        refreshStrategy,
                        values[0],
                        onUnloadWindow
                    ));

                const bootstrapConfig = {
                    sessionData: values[0],
                    userBootSettingsData: values[1],
                    bootStrings: values[2],
                };
                // This should perform module specific rebootstrap steps irrespective of settings change
                // like, update view stores with latest values from server
                moduleRebootstrapCallback(bootstrapConfig);
            })
            .catch(() => {
                // Silent fail
            });
    }

    // Adding event listener to store data in session storage when the user refreshes the tab
    window.addEventListener('unload', () => onUnloadWindowActions(onUnloadWindow));
}
